import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { forumAPI } from '../../services/api';
import { API_CONFIG } from '../../config/api';
import './ForumCreateForm.css';

const ForumEditForm = ({ forum, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    attachments: [],
    existingAttachments: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState(null);

  useEffect(() => {
    if (forum) {
      console.log('Forum data for editing:', forum);
      console.log('Forum attachments:', forum.attachments);
      setFormData({
        title: forum.title || '',
        description: forum.description || '',
        attachments: [],
        existingAttachments: forum.attachments || []
      });
    }
  }, [forum]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const totalAttachments = formData.attachments.length + formData.existingAttachments.length + files.length;
    if (totalAttachments > 5) {
      setError(`You can have a maximum of 5 attachments total. You currently have ${formData.existingAttachments.length} existing attachments and ${formData.attachments.length} new attachments selected.`);
      return;
    }
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      const totalAttachments = formData.attachments.length + formData.existingAttachments.length + files.length;
      if (totalAttachments > 5) {
        setError(`You can have a maximum of 5 attachments total. You currently have ${formData.existingAttachments.length} existing attachments and ${formData.attachments.length} new attachments selected.`);
        return;
      }
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...files]
      }));
    }
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleDeleteExistingAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    try {
      setDeletingAttachment(attachmentId);
      const response = await forumAPI.deleteAttachment(forum.id, attachmentId);
      
      if (response.success) {
        setFormData(prev => ({
          ...prev,
          existingAttachments: prev.existingAttachments.filter(att => att.id !== attachmentId)
        }));
      } else {
        setError(response.message || 'Failed to delete attachment');
      }
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setError(err.data?.message || err.message || 'Failed to delete attachment');
    } finally {
      setDeletingAttachment(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Frontend validation
    if (!formData.description.trim()) {
      setError('Please provide a description for your question.');
      setLoading(false);
      return;
    }

    if (formData.description.trim().length < 10) {
      setError('Description must be at least 10 characters long.');
      setLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title || '');
      formDataToSend.append('description', formData.description || '');

      formData.attachments.forEach((file) => {
        formDataToSend.append('attachments[]', file);
      });

      console.log('Sending forum update data:', {
        title: formData.title,
        description: formData.description,
        attachments: formData.attachments.length,
        existingAttachments: formData.existingAttachments.length
      });

      // Debug FormData contents
      console.log('FormData contents:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(key, value);
      }

      const response = await forumAPI.updateForum(forum.id, formDataToSend);

      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || 'Failed to update forum post');
      }
    } catch (err) {
      console.error('Error updating forum post:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.errors) {
        const errorMessages = Object.values(err.response.data.errors).flat();
        setError(errorMessages.join(', '));
      } else if (err.data?.message) {
        setError(err.data.message);
      } else if (err.data?.errors) {
        const errorMessages = Object.values(err.data.errors).flat();
        setError(errorMessages.join(', '));
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to update forum post. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!forum) {
    return null;
  }

  return (
    <div className="forum-create-overlay">
      <div className="forum-create-modal">
        <div className="forum-create-header">
          <h2>Edit Question</h2>
          <button
            className="close-btn"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="forum-create-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="title">Title (Optional)</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Brief title for your question"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Question Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe your question in detail..."
              rows="6"
              required
              className="form-textarea"
            />
            <small className="form-help">
              Minimum 10 characters. Be as specific as possible to get better help.
            </small>
          </div>

          {/* Existing Attachments */}
          {formData.existingAttachments && formData.existingAttachments.length > 0 && (
            <div className="form-group">
              <label>Existing Attachments</label>
              <div className="attachments-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '16px', marginTop: '8px' }}>
                {formData.existingAttachments.map((attachment) => {
                  console.log('Rendering attachment:', attachment);
                  const isImage = attachment.mime_type && attachment.mime_type.startsWith('image/');
                  return (
                    <div key={attachment.id} className="screenshot-content" style={{ position: 'relative' }}>
                      <div className="screenshot-preview">
                        {isImage ? (
                          <img
                            src={attachment.url || `${API_CONFIG.PUBLIC_URL || API_CONFIG.BASE_URL.replace('/api', '')}/storage/${attachment.file_path}`}
                            alt={attachment.original_name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block'
                            }}
                            onLoad={() => console.log('✅ Existing image loaded:', attachment.url || `${API_CONFIG.PUBLIC_URL || API_CONFIG.BASE_URL.replace('/api', '')}/storage/${attachment.file_path}`)}
                            onError={(e) => {
                              console.log('❌ Existing image failed to load:', attachment.url || `${API_CONFIG.PUBLIC_URL || API_CONFIG.BASE_URL.replace('/api', '')}/storage/${attachment.file_path}`);
                              console.log('Attachment data:', attachment);
                              // Try to construct URL manually if it fails
                              if (!attachment.url && attachment.file_path) {
                                e.target.src = `${API_CONFIG.PUBLIC_URL || API_CONFIG.BASE_URL.replace('/api', '')}/storage/${attachment.file_path}`;
                              }
                            }}
                          />
                        ) : (
                          <div style={{ padding: '20px', textAlign: 'center', background: '#f3f4f6' }}>
                            <i className="bi bi-file-earmark" style={{ fontSize: '2rem', color: '#6b7280' }}></i>
                          </div>
                        )}
                        <div className="screenshot-overlay">
                          <div className="overlay-actions">
                            <button
                              type="button"
                              onClick={() => handleDeleteExistingAttachment(attachment.id)}
                              className="overlay-btn remove-btn"
                              title="Delete this attachment"
                              disabled={deletingAttachment === attachment.id}
                            >
                              {deletingAttachment === attachment.id ? (
                                <i className="bi bi-hourglass-split"></i>
                              ) : (
                                <i className="bi bi-trash"></i>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="screenshot-info">
                        <div className="screenshot-name" title={attachment.original_name}>
                          {attachment.original_name && attachment.original_name.length > 15
                            ? attachment.original_name.substring(0, 15) + '...'
                            : attachment.original_name || 'Attachment'}
                        </div>
                        {attachment.file_size && (
                          <div className="screenshot-size">{formatFileSize(attachment.file_size)}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Add New Attachments (Optional)</label>
            <div
              className={`file-drop-zone ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => {
                const totalAttachments = formData.attachments.length + formData.existingAttachments.length;
                if (totalAttachments < 5) {
                  document.getElementById('file-input-edit').click();
                } else {
                  setError(`You can have a maximum of 5 attachments total. You currently have ${formData.existingAttachments.length} existing attachments and ${formData.attachments.length} new attachments selected.`);
                }
              }}
            >
              {formData.attachments.length > 0 ? (
                <div className="attachments-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '16px' }}>
                  {formData.attachments.map((file, index) => (
                    <div key={index} className="screenshot-content">
                      <div className="screenshot-preview">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                        <div className="screenshot-overlay">
                          <div className="overlay-actions">
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className="overlay-btn remove-btn"
                              title="Remove this image"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="screenshot-info">
                        <div className="screenshot-name" title={file.name}>
                          {file.name.length > 15
                            ? file.name.substring(0, 15) + '...'
                            : file.name}
                        </div>
                        <div className="screenshot-size">{formatFileSize(file.size)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="upload-box">
                  <div className="upload-content">
                    <div className="upload-icon">
                      <i className="bi bi-cloud-upload"></i>
                    </div>
                    <div className="upload-text">
                      <h6>Add More Attachments</h6>
                      <p>Click to upload or drag & drop</p>
                      <small>
                        Supported: JPEG, PNG, JPG, WEBP — Max 2MB each, up to {5 - formData.existingAttachments.length - formData.attachments.length} more files
                      </small>
                    </div>
                  </div>
                </div>
              )}

              <input
                id="file-input-edit"
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileChange}
                className="file-input"
                style={{ display: 'none' }}
              />
            </div>
            {formData.attachments.length + formData.existingAttachments.length >= 5 && (
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '8px', 
                marginTop: '8px' 
              }}>
                <small style={{ color: '#dc2626', fontWeight: '500' }}>
                  <i className="bi bi-exclamation-triangle" style={{ marginRight: '4px' }}></i>
                  Maximum of 5 attachments reached. Delete existing attachments to add more.
                </small>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !formData.description.trim()}
            >
              {loading ? 'Updating...' : 'Update Question'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForumEditForm;

